import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Switch,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import GooglePlacesAutocomplete from '../GooglePlacesAutocomplete';
import OptimizedCityAutocomplete from '../OptimizedCityAutocomplete';
import { GET, PATCH, DELETE, UPLOAD } from '../../configs/api';
import { api, states } from '../../utils/constants';
import { canEditCompanyProfile } from '../../utils/permissions';

const formatZipCode = (value) => {
  if (!value) return '';
  const zipCode = value.replace(/\D/g, '');
  if (zipCode.length > 5) {
    const firstPart = zipCode.substring(0, 5);
    const secondPart = zipCode.substring(5, 9);
    return secondPart ? `${firstPart}-${secondPart}` : firstPart;
  }
  return zipCode;
};

const EMPTY_PROFILE = {
  name: '',
  address: '',
  address_2: '',
  city: '',
  state: '',
  zip_code: '',
  latitude: null,
  longitude: null,
  email: '',
  phone: '',
  ein: '',
  tax_number: '',
};

const ATTACHMENT_CATEGORIES = [
  { value: 'license', label: 'License' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'tax', label: 'Tax' },
  { value: 'w9', label: 'W9' },
  { value: 'others', label: 'Others' },
];

const buildApiPaths = (mode, companyId) => {
  if (mode === 'platform') {
    const base = `/companies/${companyId}`;
    return {
      profile: `${base}/profile`,
      logo: `${base}/logo`,
      attachments: `${base}/attachments`,
      attachment: (id) => `${base}/attachments/${id}`,
      attachmentDownload: (id) => `${base}/attachments/${id}/download`,
    };
  }
  return {
    profile: api.updateCompanyProfile,
    logo: api.uploadCompanyLogo,
    attachments: api.getCompanyAttachments,
    attachment: (id) => `${api.updateCompanyAttachment}/${id}`,
    attachmentDownload: (id) => `${api.downloadCompanyAttachment}/${id}/download`,
  };
};

/**
 * Shared company profile editor for Settings (tenant) and SuperAdminCompanyPicker (platform).
 */
const CompanyProfileSection = ({ mode = 'tenant', companyId, user, onSaved, onError }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const readOnly = !canEditCompanyProfile(user);
  const paths = buildApiPaths(mode, companyId);
  const logoInputRef = useRef(null);
  const attachmentInputRef = useRef(null);

  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [errors, setErrors] = useState({});
  const [attachmentCategory, setAttachmentCategory] = useState('others');
  const [attachmentPrivate, setAttachmentPrivate] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);

  const loadData = useCallback(async () => {
    if (mode === 'platform' && !companyId) return;
    setLoading(true);
    try {
      const [profileRes, attachmentsRes] = await Promise.all([
        GET(paths.profile),
        GET(paths.attachments),
      ]);

      if (profileRes.error) {
        onError?.(profileRes.message || 'Failed to load company profile');
      } else if (profileRes.data?.company) {
        const c = profileRes.data.company;
        setProfile({
          name: c.name || '',
          address: c.address || '',
          address_2: c.address_2 || '',
          city: c.city || '',
          state: c.state || '',
          zip_code: c.zip_code || '',
          latitude: c.latitude ?? null,
          longitude: c.longitude ?? null,
          email: c.email || '',
          phone: c.phone || '',
          ein: c.ein || '',
          tax_number: c.tax_number || '',
        });
        setLogoPreviewUrl(c.logo_download_url || null);
      }

      if (!attachmentsRes.error) {
        setAttachments(attachmentsRes.data?.data?.attachments || []);
      }
    } finally {
      setLoading(false);
    }
  }, [mode, companyId, paths.profile, paths.attachments, onError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePlaceSelect = (addressData) => {
    const formattedZip = (addressData.zipCode && addressData.zipCode.trim())
      ? formatZipCode(addressData.zipCode)
      : '';
    setProfile((prev) => ({
      ...prev,
      address: addressData.address || prev.address,
      city: (addressData.city && addressData.city.trim()) ? addressData.city : '',
      state: (addressData.state && addressData.state.trim()) ? addressData.state : '',
      zip_code: formattedZip,
      latitude: addressData.latitude !== null && addressData.latitude !== undefined ? addressData.latitude : null,
      longitude: addressData.longitude !== null && addressData.longitude !== undefined ? addressData.longitude : null,
    }));
  };

  const validateProfile = () => {
    const newErrors = {};
    if (!profile.name?.trim()) newErrors.name = 'Company name is required';
    if (profile.address?.trim()) {
      if (profile.address.trim().length < 3) newErrors.address = 'Address must be at least 3 characters';
      if (!profile.city?.trim()) newErrors.city = 'City is required';
      if (!profile.state?.trim()) newErrors.state = 'State is required';
      const zipRegex = /^\d{5}(-\d{4})?$/;
      if (!zipRegex.test(profile.zip_code || '')) {
        newErrors.zip_code = 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)';
      }
    }
    if (profile.address_2 && profile.address_2.length > 500) {
      newErrors.address_2 = 'Address line 2 must not exceed 500 characters';
    }
    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateProfile()) return;
    setSaving(true);
    try {
      const res = await PATCH(paths.profile, {}, { ...profile });
      if (res.error) {
        onError?.(res.message || 'Failed to save profile');
        return;
      }
      onSaved?.('Company profile saved');
      if (res.data?.company) {
        setProfile((prev) => ({
          ...prev,
          name: res.data.company.name || prev.name,
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError?.('Logo must be an image file');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await UPLOAD(paths.logo, formData);
      if (res.error) {
        onError?.(res.message || 'Failed to upload logo');
        return;
      }
      onSaved?.('Logo uploaded');
      await loadData();
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    setUploadingLogo(true);
    try {
      const res = await DELETE(paths.logo);
      if (res.error) {
        onError?.(res.message || 'Failed to delete logo');
        return;
      }
      setLogoPreviewUrl(null);
      onSaved?.('Logo removed');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', attachmentCategory);
      formData.append('is_private', attachmentPrivate ? 'true' : 'false');
      const res = await UPLOAD(paths.attachments, formData);
      if (res.error) {
        onError?.(res.message || 'Failed to upload attachment');
        return;
      }
      onSaved?.('Attachment uploaded');
      await loadData();
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const handleTogglePrivacy = async (attachment) => {
    const res = await PATCH(paths.attachment(attachment.id), {}, {
      is_private: !attachment.is_private,
    });
    if (res.error) {
      onError?.(res.message || 'Failed to update attachment');
      return;
    }
    await loadData();
  };

  const handleDeleteAttachment = async (id) => {
    setDeletingAttachmentId(id);
    try {
      const res = await DELETE(paths.attachment(id));
      if (res.error) {
        onError?.(res.message || 'Failed to delete attachment');
        return;
      }
      onSaved?.('Attachment deleted');
      await loadData();
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const handleDownload = async (attachment) => {
    const res = await GET(paths.attachmentDownload(attachment.id));
    if (res.error || !res.data?.data?.downloadUrl) {
      onError?.(res.message || 'Failed to get download link');
      return;
    }
    window.open(res.data.data.downloadUrl, '_blank');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: isDarkMode ? theme.palette.text.secondary : '#666',
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {readOnly && (
        <Alert severity="info">You have read-only access to company profile.</Alert>
      )}

      <TextField
        label="Company Name"
        value={profile.name}
        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
        error={!!errors.name}
        helperText={errors.name}
        disabled={readOnly}
        fullWidth
        sx={textFieldSx}
      />

      <GooglePlacesAutocomplete
        label="Address"
        value={profile.address || ''}
        onChange={(value) => setProfile({ ...profile, address: value })}
        onPlaceSelect={handlePlaceSelect}
        error={!!errors.address}
        helperText={errors.address}
        placeholder=""
        disabled={readOnly}
      />

      <TextField
        label="Address Line 2 (Optional)"
        value={profile.address_2 || ''}
        onChange={(e) => setProfile({ ...profile, address_2: e.target.value })}
        error={!!errors.address_2}
        helperText={errors.address_2}
        placeholder="Suite, unit, floor, etc."
        disabled={readOnly}
        fullWidth
        sx={textFieldSx}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '2 1 200px', minWidth: 160 }}>
          <OptimizedCityAutocomplete
            value={profile.city || ''}
            onChange={(value) => setProfile({ ...profile, city: value })}
            disabled={readOnly}
            renderInput={(params) => (
              <TextField
                {...params}
                label="City"
                error={!!errors.city}
                helperText={errors.city}
                sx={textFieldSx}
              />
            )}
          />
        </Box>
        <Autocomplete
          options={Object.keys(states)}
          value={profile.state || null}
          onChange={(_, value) => setProfile({ ...profile, state: value || '' })}
          disabled={readOnly}
          getOptionLabel={(option) => (option ? `${states[option]} - ${option}` : '')}
          sx={{ flex: '1.5 1 160px', minWidth: 140 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="State"
              error={!!errors.state}
              helperText={errors.state}
              sx={textFieldSx}
            />
          )}
        />
        <TextField
          label="Zip Code"
          value={profile.zip_code || ''}
          onChange={(e) => setProfile({ ...profile, zip_code: formatZipCode(e.target.value) })}
          error={!!errors.zip_code}
          helperText={errors.zip_code}
          disabled={readOnly}
          sx={{ flex: '1 1 120px', minWidth: 120, ...textFieldSx }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Email"
          type="email"
          value={profile.email}
          onChange={(e) => setProfile({ ...profile, email: e.target.value })}
          error={!!errors.email}
          helperText={errors.email}
          disabled={readOnly}
          sx={{ flex: 1, minWidth: 200, ...textFieldSx }}
        />
        <TextField
          label="Phone"
          value={profile.phone}
          onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
          disabled={readOnly}
          sx={{ flex: 1, minWidth: 200, ...textFieldSx }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="EIN"
          value={profile.ein}
          onChange={(e) => setProfile({ ...profile, ein: e.target.value })}
          disabled={readOnly}
          inputProps={{ maxLength: 10 }}
          sx={{ flex: 1, minWidth: 160, ...textFieldSx }}
        />
        <TextField
          label="Tax Number"
          value={profile.tax_number}
          onChange={(e) => setProfile({ ...profile, tax_number: e.target.value })}
          disabled={readOnly}
          sx={{ flex: 1, minWidth: 160, ...textFieldSx }}
        />
      </Box>

      {!readOnly && (
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveProfile}
          disabled={saving}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      )}

      <Divider />

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Company Logo
        </Typography>
        {logoPreviewUrl && (
          <Box sx={{ mb: 2 }}>
            <Box
              component="img"
              src={logoPreviewUrl}
              alt="Company logo"
              sx={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            />
          </Box>
        )}
        {!readOnly && (
          <Stack direction="row" spacing={1} alignItems="center">
            <input
              ref={logoInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              type="file"
              onChange={handleLogoUpload}
            />
            <Button
              variant="outlined"
              disabled={uploadingLogo}
              onClick={() => logoInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              {uploadingLogo ? 'Uploading...' : logoPreviewUrl ? 'Replace Logo' : 'Upload Logo'}
            </Button>
            {logoPreviewUrl && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDeleteLogo}
                disabled={uploadingLogo}
                sx={{ textTransform: 'none' }}
              >
                Remove Logo
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Company Attachments
        </Typography>

        {!readOnly && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={attachmentCategory}
                label="Category"
                onChange={(e) => setAttachmentCategory(e.target.value)}
              >
                {ATTACHMENT_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={attachmentPrivate}
                  onChange={(e) => setAttachmentPrivate(e.target.checked)}
                  size="small"
                />
              }
              label="Private (hidden from admins/dispatchers)"
            />
            <input
              ref={attachmentInputRef}
              style={{ display: 'none' }}
              type="file"
              onChange={handleAttachmentUpload}
            />
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={uploadingAttachment}
              onClick={() => attachmentInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              {uploadingAttachment ? 'Uploading...' : 'Upload File'}
            </Button>
          </Stack>
        )}

        {attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No attachments yet.</Typography>
        ) : (
          <Stack spacing={1}>
            {attachments.map((att) => (
              <Box
                key={att.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#fafafa',
                }}
              >
                <AttachFileIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {att.file_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {att.category}{att.is_private ? ' · Private' : ' · Public'}
                  </Typography>
                </Box>
                {!readOnly && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!att.is_private}
                        onChange={() => handleTogglePrivacy(att)}
                      />
                    }
                    label="Private"
                    sx={{ mr: 0 }}
                  />
                )}
                <IconButton size="small" onClick={() => handleDownload(att)} title="Download">
                  <DownloadIcon fontSize="small" />
                </IconButton>
                {!readOnly && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteAttachment(att.id)}
                    disabled={deletingAttachmentId === att.id}
                    title="Delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default CompanyProfileSection;
